import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { canArchive } from 'src/components/UtilitySetupSection/companySetups';
import { setupEntries } from 'src/components/UtilitySetupSection/historyLines';
import { Needs } from 'src/components/UtilitySetupSection/parts';
import { Failed, HistoryList, Loading } from 'src/components/UtilitySetupSection/SetupHistoryPanel';
import { apPushNeeds, billToResidentsLabel, dayLabel, frequencyLabel, lastChangeLabel, pendingEditChanges, sourceLabel, sourceNeeds, usedByLabel } from 'src/components/UtilitySetupSection/setupSummary';
import { readSetupHistory } from 'src/stores/utilitySetups/companySetupsRead';
import useStyles from './styles';

/** A setup's history (LB-6180), read only once it's opened, and again after any setup change. */
const SetupHistory = _ref => {
  let companyId = _ref.companyId,
    setupId = _ref.setupId,
    version = _ref.version;
  const classes = useStyles();
  // undefined while it's read, null when it can't be.
  const _useState = useState({
      key: null,
      history: undefined
    }),
    _useState2 = _slicedToArray(_useState, 2),
    read = _useState2[0],
    setRead = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    attempt = _useState4[0],
    setAttempt = _useState4[1];
  const key = "".concat(companyId, "|").concat(setupId);
  useEffect(() => {
    let current = true;
    readSetupHistory(companyId, setupId).then(history => {
      if (current) setRead({
        key,
        history
      });
    });
    return () => {
      current = false;
    };
  }, [companyId, setupId, key, version, attempt]);
  const history = read.key === key ? read.history : undefined;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.history
  }, history === undefined && /*#__PURE__*/React.createElement(Loading, null), history === null && /*#__PURE__*/React.createElement(Failed, {
    text: "Couldn't load this setup's history.",
    onRetry: () => {
      setRead({
        key,
        history: undefined
      });
      setAttempt(value => value + 1);
    }
  }), history && /*#__PURE__*/React.createElement(HistoryList, {
    title: "History",
    items: setupEntries(history),
    empty: "No changes recorded yet."
  }));
};
const SetupRow = _ref2 => {
  var _lastChangeLabel;
  let setup = _ref2.setup,
    columns = _ref2.columns,
    isAppfolio = _ref2.isAppfolio,
    allows = _ref2.allows,
    companyId = _ref2.companyId,
    version = _ref2.version,
    actions = _ref2.actions,
    historyOpen = _ref2.historyOpen,
    busy = _ref2.busy;
  const classes = useStyles();
  const sourceNeed = sourceNeeds(setup.billSource, allows);
  const apPushNeed = apPushNeeds(allows);
  const edit = setup.pendingEdit;
  const editChanges = pendingEditChanges(setup);
  const editable = setup.hiddenProperties === 0 && !setup.isArchived;
  const hasDetail = !!edit || setup.hiddenProperties > 0 || historyOpen;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TableRow, {
    className: hasDetail ? classes.withDetail : undefined,
    "data-testid": "setup-row-".concat(setup.id)
  }, /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "div",
    className: classes.setupName
  }, setup.name), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "div",
    color: "light"
  }, setup.utilityTypeName || 'Other')), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement("span", {
    className: classes.value
  }, sourceLabel(setup.billSource), sourceNeed ? /*#__PURE__*/React.createElement(Needs, {
    tier: sourceNeed,
    where: "Utility setups page",
    className: classes.needs
  }) : null)), /*#__PURE__*/React.createElement(TableCell, null, billToResidentsLabel(setup)), /*#__PURE__*/React.createElement(TableCell, null, frequencyLabel(setup.billFrequencyType)), isAppfolio && /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement("span", {
    className: classes.value
  }, setup.isApPushEnabled ? 'On' : 'Off', apPushNeed ? /*#__PURE__*/React.createElement(Needs, {
    tier: apPushNeed,
    where: "Utility setups page",
    className: classes.needs
  }) : null)), /*#__PURE__*/React.createElement(TableCell, null, usedByLabel(setup), setup.pendingLinks > 0 && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "div",
    color: "light"
  }, setup.pendingLinks, " scheduled to join")), /*#__PURE__*/React.createElement(TableCell, null, (_lastChangeLabel = lastChangeLabel(setup)) !== null && _lastChangeLabel !== void 0 ? _lastChangeLabel : '—'), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, editable && actions.onEdit && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: () => actions.onEdit(setup),
    disabled: busy
  }, "Edit"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    "aria-expanded": historyOpen,
    onClick: () => actions.onToggleHistory(setup.id)
  }, historyOpen ? 'Hide history' : 'History'), actions.onArchive && canArchive(setup) && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    onClick: () => actions.onArchive(setup),
    disabled: busy
  }, "Archive")))), hasDetail && /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    colSpan: columns,
    className: classes.detailCell
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.detail
  }, edit && /*#__PURE__*/React.createElement("div", {
    className: classes.detailLine
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "An edit to this setup applies from ", dayLabel(edit.effectiveFrom), edit.changedByName ? ", scheduled by ".concat(edit.changedByName) : '', editChanges.length ? ": ".concat(editChanges.join(', '), ".") : '.'), editable && actions.onCancelEdit && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    onClick: () => actions.onCancelEdit(setup),
    disabled: busy
  }, "Cancel edit")), setup.hiddenProperties > 0 && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "You can't edit this setup: it also drives ", setup.hiddenProperties, ' ', setup.hiddenProperties === 1 ? 'property' : 'properties', " you can't see."), historyOpen && /*#__PURE__*/React.createElement(SetupHistory, {
    companyId: companyId,
    setupId: setup.id,
    version: version
  })))));
};

/**
 * The company's setups as the PRD V3 mockup lists them (LB-6193): the setup and its type, how bills reach Livable, Bill
 * to residents, frequency, AP Push for AppFolio companies, how many utilities follow it, and its last change. A pending
 * edit, a note on properties the caller can't see, and an opened history show under its row.
 *
 * `actions` holds the row actions this list offers: `onToggleHistory` always, and `onEdit`, `onArchive` and
 * `onCancelEdit` for live setups.
 */
export const SetupsTable = _ref3 => {
  let setups = _ref3.setups,
    label = _ref3.label,
    isAppfolio = _ref3.isAppfolio,
    allows = _ref3.allows,
    companyId = _ref3.companyId,
    version = _ref3.version,
    actions = _ref3.actions,
    openHistories = _ref3.openHistories,
    busy = _ref3.busy;
  const classes = useStyles();
  const columns = isAppfolio ? 8 : 7;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.panel
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.tableScroll
  }, /*#__PURE__*/React.createElement(Table, {
    size: "small",
    className: classes.table,
    "aria-label": label
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Setup"), /*#__PURE__*/React.createElement(TableCell, null, "How bills reach Livable"), /*#__PURE__*/React.createElement(TableCell, null, "Bill to residents"), /*#__PURE__*/React.createElement(TableCell, null, "Frequency"), isAppfolio && /*#__PURE__*/React.createElement(TableCell, null, "AP Push"), /*#__PURE__*/React.createElement(TableCell, null, "Used by"), /*#__PURE__*/React.createElement(TableCell, null, "Last change"), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement("span", {
    className: classes.visuallyHidden
  }, "Actions")))), /*#__PURE__*/React.createElement(TableBody, null, setups.map(setup => /*#__PURE__*/React.createElement(SetupRow, {
    key: setup.id,
    setup: setup,
    columns: columns,
    isAppfolio: isAppfolio,
    allows: allows,
    companyId: companyId,
    version: version,
    actions: actions,
    historyOpen: openHistories.includes(setup.id),
    busy: busy
  }))))));
};